import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Skeleton } from './ui/skeleton';
import { 
  CheckCircle2, 
  XCircle, 
  TrendingUp, 
  Calendar,
  Target,
  BarChart3,
  Info,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LeagueAccuracyModal({ 
  isOpen, 
  onClose, 
  leagueId, 
  leagueName,
  isPlatform = true // true for platform accuracy, false for user accuracy
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && leagueId) {
      fetchLeagueAccuracy();
    }
  }, [isOpen, leagueId, isPlatform]);

  const fetchLeagueAccuracy = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const endpoint = isPlatform 
        ? `${API_URL}/api/accuracy/league/${leagueId}`
        : `${API_URL}/api/accuracy/user/league/${leagueId}`;
      
      const response = await fetch(endpoint, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError('Failed to load accuracy data');
      }
    } catch (err) {
      setError('Failed to load accuracy data');
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceColor = (accuracy) => {
    if (accuracy >= 60) return 'text-green-400';
    if (accuracy >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getConfidenceBadgeColor = (confidence) => {
    switch (confidence) {
      case 'High': return 'bg-green-500/20 text-green-400';
      case 'Medium': return 'bg-yellow-500/20 text-yellow-400';
      case 'Low': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] bg-card border-border" data-testid="league-accuracy-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <TrendingUp className="w-5 h-5 text-green-400" />
            {leagueName || leagueId?.toUpperCase()} - {isPlatform ? 'Platform' : 'Your'} Accuracy
          </DialogTitle>
          <DialogDescription>
            Detailed prediction breakdown by confidence level and game results
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-400">{error}</div>
        ) : data ? (
          <div className="space-y-6">
            {/* Overall Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-secondary/30 rounded-sm text-center">
                <p className="text-xs text-muted-foreground uppercase mb-1">Total Predictions</p>
                <p className="text-3xl font-bold font-mono">{data.total_predictions}</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-sm text-center">
                <p className="text-xs text-muted-foreground uppercase mb-1">Correct</p>
                <p className="text-3xl font-bold font-mono text-green-400">{data.correct_predictions}</p>
              </div>
              <div className="p-4 bg-secondary/30 rounded-sm text-center">
                <p className="text-xs text-muted-foreground uppercase mb-1">Accuracy</p>
                <p className={`text-3xl font-bold font-mono ${getConfidenceColor(data.accuracy_percentage)}`}>
                  {data.accuracy_percentage}%
                </p>
              </div>
            </div>

            {/* MLB Spring Training Notice */}
            {leagueId === 'mlb' && data.total_predictions === 0 && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm" data-testid="mlb-spring-training-notice">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-500/20 rounded-full">
                    <Info className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-400 mb-2">MLB Spring Training - Accuracy Tracking Paused</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Spring Training games are <strong>excluded</strong> from platform accuracy because:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-2">
                      <li className="flex items-center gap-2">
                        <X className="w-4 h-4 text-red-400" />
                        <span>No established team win/loss records</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <X className="w-4 h-4 text-red-400" />
                        <span>Inconsistent rosters (not regular season lineups)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <X className="w-4 h-4 text-red-400" />
                        <span>Spring training facility venues (not regular MLB parks)</span>
                      </li>
                    </ul>
                    <div className="mt-3 pt-3 border-t border-amber-500/20">
                      <p className="text-xs text-amber-400/80">
                        <strong>Note:</strong> Predictions are still generated for Spring Training games. 
                        MLB accuracy tracking will begin with the regular season (Opening Day 2026).
                      </p>
                    </div>
                    {data.excluded_spring_training > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {data.excluded_spring_training} Spring Training game{data.excluded_spring_training !== 1 ? 's' : ''} excluded from accuracy.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Confidence Breakdown */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Accuracy by Confidence Level
              </h3>
              <div className="grid grid-cols-3 gap-4">
                {['High', 'Medium', 'Low'].map((conf) => {
                  const confData = data.by_confidence?.[conf] || { total: 0, correct: 0, accuracy: 0 };
                  return (
                    <div 
                      key={conf}
                      className="p-4 bg-secondary/20 rounded-sm border border-border"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge className={getConfidenceBadgeColor(conf)}>{conf}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {confData.correct}/{confData.total}
                        </span>
                      </div>
                      <p className={`text-2xl font-bold font-mono ${getConfidenceColor(confData.accuracy)}`}>
                        {confData.accuracy}%
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Game-by-Game Results */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Game Predictions vs Actuals
              </h3>
              <ScrollArea className="h-[300px] pr-4">
                {data.game_predictions && data.game_predictions.length > 0 ? (
                  <div className="space-y-2">
                    {data.game_predictions.map((game, idx) => (
                      <div 
                        key={game.game_id || idx}
                        className={`p-3 rounded-sm border cursor-pointer hover:opacity-80 transition-opacity ${
                          game.is_correct 
                            ? 'bg-green-500/5 border-green-500/20 hover:bg-green-500/10' 
                            : 'bg-red-500/5 border-red-500/20 hover:bg-red-500/10'
                        }`}
                        data-testid={`game-prediction-row-${idx}`}
                        onClick={() => {
                          if (game.game_id) {
                            onClose();
                            window.location.href = `/game/${game.game_id}`;
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && game.game_id) {
                            onClose();
                            window.location.href = `/game/${game.game_id}`;
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {game.is_correct ? (
                              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                            )}
                            <div>
                              <p className="font-medium text-sm">
                                {game.away_team_abbr || game.away_team} @ {game.home_team_abbr || game.home_team}
                              </p>
                              <p className="text-xs text-muted-foreground">{game.date}</p>
                            </div>
                          </div>
                          <Badge className={getConfidenceBadgeColor(game.confidence)}>
                            {game.confidence}
                          </Badge>
                        </div>
                        
                        <div className="mt-2 grid grid-cols-3 gap-4 text-xs">
                          <div>
                            <p className="text-muted-foreground">Prediction</p>
                            <p className="font-medium">
                              {game.predicted_winner === 'home' 
                                ? game.home_team_name || game.home_team_abbr || 'Home Team' 
                                : game.predicted_winner === 'away'
                                  ? game.away_team_name || game.away_team_abbr || 'Away Team'
                                  : game.predicted_winner === 'tie' 
                                    ? 'Tie (±5)'
                                    : game.predicted_winner}
                              <span className="text-muted-foreground ml-1">
                                ({game.predicted_winner === 'home' 
                                  ? game.home_probability 
                                  : game.away_probability}%)
                              </span>
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Actual Winner</p>
                            <p className="font-medium">
                              {game.actual_winner === 'home' 
                                ? game.home_team_name || game.home_team_abbr || 'Home Team' 
                                : game.away_team_name || game.away_team_abbr || 'Away Team'}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Final Score</p>
                            <p className="font-medium font-mono">
                              {game.away_score ?? '-'} - {game.home_score ?? '-'}
                            </p>
                          </div>
                        </div>
                        
                        {!isPlatform && game.agreed_with_platform !== undefined && (
                          <div className="mt-2 pt-2 border-t border-border">
                            <p className="text-xs">
                              <span className="text-muted-foreground">Platform Agreement: </span>
                              <span className={game.agreed_with_platform ? 'text-blue-400' : 'text-orange-400'}>
                                {game.agreed_with_platform ? 'Agreed' : 'Disagreed'}
                              </span>
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No game predictions available for this league
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default LeagueAccuracyModal;
